import { Component, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/components/base.component';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { LexbeDoc } from '@models/doc.model';
import { RedactionStatus } from '@models/redaction-status.enum';
import { RedactionUploadStrategy } from '@utilities/manual-upload-strategies/redaction-upload-strategy';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { NotificationManager } from '@managers/notification.manager';
import { ManualUploadDialogModel } from '../../dialogs/manual-upload-dialog/manual-upload-dialog.model';
import { ManualUploadDialogComponent } from '../../dialogs/manual-upload-dialog/manual-upload-dialog.component';
import { OkCancelDialogModel } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog.model';
import { OkCancelDialogNewComponent } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog-new.component';
import { Store } from '@ngrx/store';
import { currentRedactionEditorPage, currentRedactionStatus, isEditorProcessing } from '@store/doc-viewer/redaction-editor/redaction-editor.selectors';
import { currentAnnotationStatus, isAnnotationEditorProcessing } from '@store/doc-viewer/annotation-editor/annotation-editor.selectors';
import { currentDocumentView } from '@store/doc-viewer/document-view/document-view.selectors';
import { DocumentViews } from '@store/doc-viewer/doc-viewer.state';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { documentViewActions } from '@store/doc-viewer/document-view/document-view.actions';
import { redactionEditorActions } from '@store/doc-viewer/redaction-editor/redaction-editor.actions';
import { annotationEditorActions } from '@store/doc-viewer/annotation-editor/annotation-editor.actions';
import { AnnotationUploadStrategy } from '@utilities/manual-upload-strategies/annotation-upload-strategy';
import { AnnotationStatus } from '@models/annotation-status.enum';
import { NativeRedactionUploadStrategy } from '@utilities/manual-upload-strategies/native-redaction-upload-strategy';

@Component({
    selector: 'lex-doc-viewer-editor-tab',
    templateUrl: './doc-viewer-editor-tab.component.html',
    styleUrls: ['./doc-viewer-editor-tab.component.scss'],
    standalone: false
})
export class DocViewerEditorTabComponent extends BaseComponent implements OnInit {
	public tabIndex: number = 0;
	public currentPage: number = 1;
	public redactionEditorStatus$: Observable<RedactionStatus> = this.store.select(currentRedactionStatus());
	public redactionEditorPageNumber$: Observable<number> = this.store.select(currentRedactionEditorPage());
	public selectedView$: Observable<DocumentViews> = this.store.select(currentDocumentView());
	public isAnnotationProcessing: boolean;
	public isRedactionProcessing: boolean;
	public  readonly RedactionStatus = RedactionStatus;
	
	get hasNativeRedaction(): boolean {
		return (this.redactionStatus & RedactionStatus.HasRedactedNative) !== 0;
	  }
	get hasManualRedactedPdf(): boolean {
		return this.redactionStatus === RedactionStatus.HasPdf;
	}
	get hasRedaction(): boolean {
		return (this.redactionStatus & RedactionStatus.HasImages) !== 0;
	}

	private annotationEditorStatus$: Observable<AnnotationStatus> = this.store.select(currentAnnotationStatus);
	private isAnnotationProcessing$ = this.store.select(isAnnotationEditorProcessing);
	private isAnnotated: boolean;
	private annotationStatus: AnnotationStatus = AnnotationStatus.Unknown;
	private isRedactionProcessing$ = this.store.select(isEditorProcessing);
	private isRedacted: boolean;
	private redactionStatus: RedactionStatus = RedactionStatus.Unknown;
	private currentDoc: LexbeDoc;

	constructor(private store: Store,
		private matDialog: MatDialog,
		private docViewerStore: DocViewerStore,
		private annotationUploadStrategy: AnnotationUploadStrategy,
		private redactionUploadStrategy: RedactionUploadStrategy,
		private nativeRedactionUploadStrategy: NativeRedactionUploadStrategy,
		private notificationManager: NotificationManager) {
		super();
		this.docViewerStore.currentDocument$.pipe(
			map(doc => this.currentDoc = doc),
			takeUntil(this.destroy$)
		).subscribe();
	}

	public ngOnInit(): void {
		this.selectedView$.pipe(
			map((view) => {
				if (view === 'redacted')
					this.tabIndex = 0;
				else if (view === 'annotated')
					this.tabIndex = 1;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.annotationEditorStatus$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((status) => {
				this.annotationStatus = status;
				this.isAnnotated = (status & AnnotationStatus.HasPdf) ? true : false;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.isAnnotationProcessing$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((status) => this.isAnnotationProcessing = status),
			takeUntil(this.destroy$)
		).subscribe();

		this.redactionEditorPageNumber$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((page) => {
				this.currentPage = page;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.redactionEditorStatus$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((status) => {
				this.redactionStatus = status;
				this.isRedacted = (status & RedactionStatus.HasPdf) ? true : false;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.isRedactionProcessing$.pipe(
			map((isProcessing) => this.isRedactionProcessing = isProcessing),
			takeUntil(this.destroy$)
		).subscribe();
	}

	public tabChange(tab: number): void {
		this.tabIndex = tab;
		const view: DocumentViews = this.tabIndex === 0 ? 'redacted' : 'annotated';
		this.store.dispatch(documentViewActions.viewUpdated({ selectedView: view }));
	}

	public viewAnnotatedDocument(): void {
		if (!this.IsCurrentDocSet()) return;

		this.store.dispatch(annotationEditorActions.viewAnnotatedPDFFile({ docId: this.currentDoc.docId }));
	}

	public openManualAnnotationUpload(): void {
		if (!this.IsCurrentDocSet()) return;

		const model = new ManualUploadDialogModel();
		model.strategy = this.annotationUploadStrategy;
		const config = new MatDialogConfig<ManualUploadDialogModel>();
		config.data = model;
		config.width = '450px';
		this.matDialog.open(ManualUploadDialogComponent, config).afterClosed().subscribe(result => {
			if (result) {
				this.store.dispatch(annotationEditorActions.getAnnotationStatus({ docId: this.currentDoc.docId }));
				this.notificationManager.displaySnackBarMessage({ message: 'Annotation doc uploaded.' });
			}
		});
	}

	public deleteAnnotation(): void {
		if (!this.IsCurrentDocSet()) return;
		if (this.isAnnotationProcessing) {
			this.notificationManager.displaySnackBarMessage({
				message: 'Cannot delete annotated document while it is being processed.'
			});
			return;
		}

		const model = new OkCancelDialogModel();
		model.title = 'Confirm';
		model.message = 'Delete Annotated Document?';
		const config = new MatDialogConfig<OkCancelDialogModel>();
		config.data = model;
		this.matDialog.open(OkCancelDialogNewComponent, config)
			.afterClosed()
			.subscribe((okClicked) => {
				if (okClicked) {
					this.store.dispatch(annotationEditorActions.resetAnnotationsForCurrentDoc({ docId: this.currentDoc.docId }))
				}
			});
	}

	public shouldDisableDeleteAnnotation(): boolean {
		if (!this.currentDoc)
			return true;
		// We only allow reseting the complete annotation status if the document has been annotated.
		return !this.isAnnotated && (this.annotationStatus !== AnnotationStatus.HasImages || this.isAnnotationProcessing);
	}

	public shouldDisableViewAnnotation(): boolean {
		if (!this.currentDoc)
			return true;
		return !this.isAnnotated && (this.annotationStatus !== AnnotationStatus.HasPdf || this.isAnnotationProcessing);
	}

	public viewRedactedDocument(): void {
		if (!this.IsCurrentDocSet()) return;

		this.store.dispatch(redactionEditorActions.viewRedactedPDFFile({ docId: this.currentDoc.docId }));
	}

	public openManualRedactionUpload(): void {
		if (!this.IsCurrentDocSet()) return;

		const model = new ManualUploadDialogModel();
		model.strategy = this.redactionUploadStrategy;
		const config = new MatDialogConfig<ManualUploadDialogModel>();
		config.data = model;
		config.width = '450px';
		this.matDialog.open(ManualUploadDialogComponent, config).afterClosed().subscribe(result => {
			if (result) {
				this.store.dispatch(redactionEditorActions.getRedactionStatus({ docId: this.currentDoc.docId }));
				this.notificationManager.displaySnackBarMessage({ message: 'Redacted doc uploaded.' });
			}
		});
	}

	public deleteRedaction(): void {
		if (!this.IsCurrentDocSet()) return;
		if (this.isRedactionProcessing) {
			this.notificationManager.displaySnackBarMessage({
				message: 'Cannot delete redacted document while it is being processed.'
			});
			return;
		}

		const model = new OkCancelDialogModel();
		model.title = 'Confirm';
		model.message = 'Delete Redacted Document?';
		const config = new MatDialogConfig<OkCancelDialogModel>();
		config.data = model;
		this.matDialog.open(OkCancelDialogNewComponent, config)
			.afterClosed()
			.subscribe((okClicked) => {
				if (okClicked) {
					this.store.dispatch(redactionEditorActions.resetRedactionsForCurrentDoc({ docId: this.currentDoc.docId }))
				}
			});
	}

	public deleteNativeRedaction(): void {
		if (!this.IsCurrentDocSet()) return;
		if (this.isRedactionProcessing) {
			this.notificationManager.displaySnackBarMessage({
				message: 'Cannot delete redacted document while it is being processed.'
			});
			return;
		}

		const model = new OkCancelDialogModel();
		model.title = 'Confirm';
		model.message = 'Delete Native Redacted File?';
		const config = new MatDialogConfig<OkCancelDialogModel>();
		config.data = model;
		this.matDialog.open(OkCancelDialogNewComponent, config)
			.afterClosed()
			.subscribe((okClicked) => {
				if (okClicked) {
					this.store.dispatch(redactionEditorActions.resetNativeRedactedFileForCurrentDoc({ docId: this.currentDoc.docId }))
				}
			});
	}

	public shouldDisableDeleteRedaction(): boolean {
		if (!this.currentDoc)
			return true;

		if (this.hasNativeRedaction)
			return true;

			// We only allow reseting the complete redaction status if the document has been redacted.
		return !this.isRedacted && (this.redactionStatus !== RedactionStatus.HasImages || this.isRedactionProcessing);
	}

	public shouldDisableViewRedaction(): boolean {
		if (!this.currentDoc)
			return true;
			
		if (this.hasNativeRedaction)
			return true;

		return !this.isRedacted && (this.redactionStatus !== RedactionStatus.HasPdf || this.isRedactionProcessing);
	}

	public shouldDisableNativeRedaction(): boolean {
		if (!this.currentDoc)
			return true;
		return !this.hasNativeRedaction || this.isRedactionProcessing;
	}

	public downloadNativeRedactedFile(): void {
		if (!this.IsCurrentDocSet()) return;

		this.store.dispatch(redactionEditorActions.viewNativeRedactedFile({ docId: this.currentDoc.docId }));
	}

	public openManualNativeRedactionUpload(): void {
		if (!this.IsCurrentDocSet()) return;

		const model = new ManualUploadDialogModel();
		model.strategy = this.nativeRedactionUploadStrategy;
		const config = new MatDialogConfig<ManualUploadDialogModel>();
		config.data = model;
		config.width = '450px';
		this.matDialog.open(ManualUploadDialogComponent, config).afterClosed().subscribe(result => {
			if (result) {
				this.store.dispatch(redactionEditorActions.getRedactionStatus({ docId: this.currentDoc.docId }));
				this.notificationManager.displaySnackBarMessage({ message: 'Redacted Native uploaded.' });
			}
		});
	}

	private IsCurrentDocSet(): boolean {
		if (!this.currentDoc) {
			this.notificationManager.displaySnackBarMessage({
				message: 'An issue has occurred, and the current document is not set. Please attempt the action again or reach out to Professional Services for assistance.'
			});
			return false;
		}
		return true;
	}
}
